import {
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  SELF_FUNDING_ASSET_TYPES,
  type AssetTypeId,
} from '@/features/funding-sources';

interface BuildSelfFundingSourceArgs {
  selfFundingState: SelfFundingSource;
  selectedAssetType: AssetTypeId | 'other';
  isOther: boolean;
  isIncomeStream: boolean;
  isSpecificAssetType: boolean;
  yearlyContributionValue: number | null;
  ltcAtAge: number | null | undefined;
}

/**
 * Build the final `SelfFundingSource` to persist from the form's working state.
 * Encodes the asset-type-driven invariants:
 *   - Specific (non-income-stream) types convert yearly contribution to monthly
 *     and clear monthly-income fields.
 *   - Income-stream types zero out contributions and drawdowns.
 *   - When drawdowns are toggled off, all related fields are cleared.
 */
export function buildSelfFundingSource({
  selfFundingState,
  selectedAssetType,
  isOther,
  isIncomeStream,
  isSpecificAssetType,
  yearlyContributionValue,
  ltcAtAge,
}: BuildSelfFundingSourceArgs): SelfFundingSource {
  const submitAssetLabel = isOther
    ? undefined
    : SELF_FUNDING_ASSET_TYPES.find(t => t.id === selectedAssetType)?.label;

  const hasDrawdown = !!selfFundingState.hasDrawdown;

  return {
    ...selfFundingState,
    hasSelfFunding: true,
    selfFundingName: isOther
      ? selfFundingState.selfFundingName || 'Self-funding'
      : (submitAssetLabel ?? 'Self-funding'),
    accountType: isOther
      ? (selfFundingState.accountType ?? null)
      : ASSET_TYPE_TO_ACCOUNT_TYPE[selectedAssetType as AssetTypeId],
    ...(isSpecificAssetType &&
      !isIncomeStream && {
        monthlyContributionAmount: (yearlyContributionValue ?? 0) / 12,
        monthlyIncome: null,
        monthlyIncomeTaxRate: null,
      }),
    ...(isIncomeStream && {
      annualRateOfReturn: selfFundingState.annualRateOfReturn ?? 0,
      incomeReferenceAge: selfFundingState.incomeReferenceAge ?? ltcAtAge ?? null,
      monthlyContributionAmount: 0,
      monthlyIncome: null,
      monthlyIncomeTaxRate: null,
      contributionStopYear: null,
      hasDrawdown: false,
      drawdownStartAtYear: null,
      drawdownEndAtYear: null,
      drawdownAmount: null,
      drawdownAmountInterval: null,
    }),
    ...(!hasDrawdown && {
      drawdownStartAtYear: null,
      drawdownEndAtYear: null,
      drawdownAmount: null,
      drawdownAmountInterval: null,
    }),
  } as SelfFundingSource;
}
